import React, { useEffect, useRef, forwardRef, useCallback, useMemo, useState } from 'react';
import { OrderedSet, List, OrderedMap, Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import flow from 'lodash/fp/flow';
import { debounce } from 'throttle-debounce';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import Api from 'domain/api';
import {
  documentsGridFilterAppliedAction,
  documentsGridSortingAppliedAction,
  documentsGridColumnsAppliedAction,
  documentsGridGetRowsAction,
  documentsGridDefaultColumnsAppliedAction,
  documentsGridDocumentsByPageIndexAction,
  gridCurrentFiltersSelector,
  gridCurrentSortSelector,
  gridCurrentColumnsStateSelector,
  gridRawPresetsLoadedSelector,
  gridApplyTransactionSelector,
  documentsGridApplyTransactionAction,
  documentsListListSelector,
  documentCountSelector,
  documentsGridSearchAfterByPageIndexAction,
  getGridSearchAfterByPageIndexSelector,
} from 'domain/documents';
import { agGridRefreshServerSideSelector, agGridRefreshServerSideAction } from 'domain/agGrid';
import { GridApplyTransactionFactory } from 'domain/documents/factories';
import { isCompanyConfigurationLoadedSelector } from 'domain/companies/companiesSelector';
import {
  changeWidthIfMasterViewNotAvailable,
  setExelModeFilter,
  addColDefaultParams,
  suppressSelectAll,
} from 'components/AgGrid/helpers';
import {
  addSelectionMasterDetailToFirstColumn,
  addColumnsVisibilityColumnDef,
  addContextMenuColumnDef,
  addPreviewColumnDef,
  addLinkedButtonColumnRef,
} from 'components/AgGrid/Documents/serviceColumnDefs';
import * as ACL from 'domain/restriction';
import { addFilters } from 'components/AgGrid/helpers/filterHelpers';
import { documentsGridRowNodesAdapter, gridDocumentsListAdapter } from 'domain/documents/adapters';
import { getNestedCategory } from 'domain/categories/helpers';
import { GRID_PAGE_SIZE } from 'domain/documents/constants';
import { getWorkspaceGridPerPage, setWorkspaceGridPerPage } from 'domain/documents/helpers';

// components
import { AgGridReact } from 'ag-grid-react';
import {
  ModuleRegistry,
  GridStateModule,
  PaginationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  ColumnApiModule,
  CellApiModule,
} from 'ag-grid-community';
import {
  CellSelectionModule,
  ColumnMenuModule,
  ContextMenuModule,
  GroupFilterModule,
  MasterDetailModule,
  MultiFilterModule,
  ServerSideRowModelModule,
  SetFilterModule,
  StatusBarModule,
  ClipboardModule,
} from 'ag-grid-enterprise';
import Box from '@mui/material/Box';
import DocumentPreviewNavigation from 'pages/company/WorkspaceRoute/components/DocumentPreviewNavigation';
import MasterViewCellRenderer from 'pages/company/WorkspaceRoute/components/Grid/components/MasterViewCellRenderer';
import PreviewCellRenderer from 'pages/company/WorkspaceRoute/components/Grid/components/PreviewCellRenderer';
import ContextCellRenderer from 'pages/company/WorkspaceRoute/components/Grid/components/ContextCellRenderer';
import ApprovalsCellRenderer from 'components/AgGrid/components/ApprovalsCellRenderer';
import TagsCellRenderer from 'components/AgGrid/components/TagsCellRenderer';
import AlertCellRenderer from 'pages/company/WorkspaceRoute/components/Grid/components/AlertCellRenderer';
import AgGridColumnHeader from 'components/AgGrid/components/ColumnHeader';
import AmountSumStatusPanelRenderer from 'pages/company/WorkspaceRoute/components/Grid/components/AmountSumStatusPanelRenderer';
import AmountCellRenderer from 'pages/company/WorkspaceRoute/components/Grid/components/AmountCellRenderer';
import CustomSetColumnFilter from 'components/AgGrid/components/CustomHeaderFilter/CustomSetColumnFilter/CustomSetColumnFilter';
import LinkedCellRenderer from 'pages/company/WorkspaceRoute/components/Grid/components/LinkedCellRenderer';
import StubWhenEmpty from 'pages/company/WorkspaceRoute/components/stub/StubWhenEmpty';

import usePropagateGridApi from 'pages/company/WorkspaceRoute/components/Grid/usePropagateGridApiRef';
import usePrevious from 'hooks/usePrevious';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';
import useScrollManager, { getWorkspaceScrollPosition } from 'lib/scrollManager';
import useAgGridDocumentsProps from 'components/AgGrid/Documents/useAgGridDocumentsProps';
import useColumnTypes from 'components/AgGrid/Documents/useAgGridColumnTypes';
import useAgGridTranslations from 'components/AgGrid/hooks/useAgGridTranslations';
import useAgGridRowClassesRules from 'components/AgGrid/Documents/useAgGridRowClassesRules';
import useAgGridRtlMethods from 'components/AgGrid/hooks/useAgGridRtlMethods';
import useGridContext from 'pages/company/WorkspaceRoute/components/Grid/useGridContext';
import useFetchGridColumnDefs from 'pages/company/WorkspaceRoute/components/Grid/useFetchGridColumnDefs';
import useRangeSelectionChanged from 'pages/company/WorkspaceRoute/components/Grid/useRangeSelectionChanged';
import useAgGridDocumentsPreviewNavigation from 'components/AgGrid/Documents/useAgGridDocumentsPreviewNavigation';
import useAgGridTheme from 'components/AgGrid/hooks/useAgGridTheme';
import { filterMapping } from 'components/AgGrid/Documents/filterHelpers';
// https://stackoverflow.com/questions/62202886/styles-injection-order-with-jss-and-material-ui-differs-between-dev-and-prod
// The main point I've missed it that <styles> are injected when makeStyles is called: injection order is the same than calls to makeStyles order.
import AgGridGlobalStyles from 'components/AgGrid/components/AgGridGlobalStyles';
import AgGridDocumentsStyles from 'components/AgGrid/Documents/AgGridDocumentsStyles';

import AgDateFilter from 'components/AgGrid/components/AgDateFilter/AgDateFilterBE';

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  MasterDetailModule,
  CellSelectionModule,
  StatusBarModule,
  ContextMenuModule,
  ColumnMenuModule,
  GridStateModule,
  PaginationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  CustomFilterModule,
  CellSelectionModule,
  ColumnApiModule,
  CellApiModule,
  ClipboardModule,
]);

export const PREVIEW_CONTEXT_NAME = 'gridList';

const mapStateToProps = (state) => ({
  documentsList: documentsListListSelector(state),
  documentsCount: documentCountSelector(state),
  isGranted: ACL.isGranted(state),
  gridFilters: gridCurrentFiltersSelector(state),
  gridSorting: gridCurrentSortSelector(state),
  gridColumnsState: gridCurrentColumnsStateSelector(state),
  gridSearchAfterByPageIndex: getGridSearchAfterByPageIndexSelector(state),
  isGridPresetLoaded: gridRawPresetsLoadedSelector(state),
  isCompanyConfigurationLoaded: isCompanyConfigurationLoadedSelector(state),
  refreshDocumentNodes: gridApplyTransactionSelector(state),
  refreshServerSide: agGridRefreshServerSideSelector(state),
});

const Grid = forwardRef(
  (
    {
      onContextMenu,
      onChangeSelected,
      onDocumentOpen,
      selected,
      onPreview,
      onShowLinked,
      resetIsLinkedPanelSelected,
      isLinkedPanelSelected,
      preview,
      isTrashCategory,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const routeParams = useParams();
    const { search } = useLocation();
    const agGridTheme = useAgGridTheme();
    const DEFAULT_PER_PAGE = useMemo(() => getWorkspaceGridPerPage(), []);

    const {
      documentsList,
      documentsCount,
      refreshDocumentNodes,
      refreshServerSide,
      isGranted,
      gridFilters,
      gridSorting,
      gridColumnsState,
      gridSearchAfterByPageIndex,
      isGridPresetLoaded,
      isCompanyConfigurationLoaded,
    } = useSelector(mapStateToProps);

    const agGridDocumentsProps = useAgGridDocumentsProps();
    const { getLocaleText } = useAgGridTranslations();
    const rowClassRules = useAgGridRowClassesRules();
    const { invertPinnedWhenRTL, enableRtl } = useAgGridRtlMethods();
    const columnTypes = useColumnTypes();
    const gridApiRef = useRef();
    // !!! Ref.current used to avoid unnecessary prop changes and re-renders in ag-grid instead of getting this data from Redux after changes
    // !!! dispatched data to redux mostly needed for propagate(sharing) to another components
    const onFirstDataRenderedRef = useRef(false);
    const filterModelRef = useRef(null); // workaround for not applied filter model in grid
    const previousFilterModelRef = useRef({}); // used for detect if filter model has changes
    const previousSortModelRef = useRef([]); // used for detect if sort model has changes
    const previousColumnModelRef = useRef([]); // used for detect if column model has changes - [width, pinned, order, hide]
    const previousInitialColumnStateRef = useRef([]); // used for detect if initial column state has changes
    const previousSelectedSize = usePrevious(selected.size); // used for detect cmd/ctrl+A or escape
    const selectedIDsRef = useRef(selected);
    const searchAfterByPageIndexRef = useRef(Map()); // keys for navigation between pages, param used by b-end for pagination for huge list of documents(over 100k for example)
    // documentsOrderedMapRef used for pagination, dispatch actual documents list to redux
    // actual documents for current page
    const documentsOrderedMapRef = useRef(new OrderedMap()); // OrderedMap with adapted documents by ID
    const [isGridInit, setGridInit] = useState(false);
    const [isFirstDataFetched, setIsFirstDataFetched] = useState(false);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [isDatasourceRequestInProgress, setDatasourceRequestInProgress] = useState(false);

    const onCellSelectionChanged = useRangeSelectionChanged(gridApiRef);
    const category = getNestedCategory(routeParams);

    const hasMasterDetail = isGranted(ACL.IS_BOOKKEEPER_IN_FINANCIAL_COMPANY) && !isTrashCategory;

    const { rawColumnDefsIsBusy, rawColumnDefs } = useFetchGridColumnDefs();

    const defaultColumnDefs = useMemo(() => {
      if (rawColumnDefsIsBusy) return [];

      const columns = flow([
        addLinkedButtonColumnRef,
        addPreviewColumnDef,
        addSelectionMasterDetailToFirstColumn,
        addColumnsVisibilityColumnDef,
        addContextMenuColumnDef,
        addFilters(filterMapping),
        setExelModeFilter('windows'),
        addColDefaultParams,
      ])(rawColumnDefs);

      const state = changeWidthIfMasterViewNotAvailable(columns, hasMasterDetail);
      previousColumnModelRef.current = state;

      return state;
    }, [rawColumnDefsIsBusy, rawColumnDefs, hasMasterDetail]);

    const columnDefs = useMemo(
      () => flow(suppressSelectAll, invertPinnedWhenRTL)(defaultColumnDefs),
      [defaultColumnDefs, invertPinnedWhenRTL],
    );

    const initialColumnState = useMemo(
      () =>
        defaultColumnDefs.map(({ field, hide = false, pinned, width = 0, minWidth, maxWidth }) => {
          const currentWidth = Math.max(minWidth || 1, Math.min(maxWidth || 1000, width));

          return {
            aggFunc: null,
            colId: field,
            flex: null,
            hide,
            pinned: pinned || null,
            pivot: false,
            pivotIndex: null,
            rowGroup: false,
            rowGroupIndex: null,
            sort: null,
            sortIndex: null,
            width: currentWidth,
          };
        }),
      [defaultColumnDefs],
    );

    const initialState = useMemo(() => {
      const { pageIndex = 0 } = getWorkspaceScrollPosition();

      return {
        filter: {
          filterModel: gridFilters,
        },
        sort: {
          sortModel: gridSorting,
        },
        pagination: { page: pageIndex, pageSize: perPage },
        partialColumnState: true,
      };
    }, [gridSorting, gridFilters, perPage]);

    // DA-19691: AG Grid needs serverSideInitialRowCount > (targetRowIndex + visible rows) to restore
    // the saved page/scroll on mount (see AG Grid Server-Side "Initial Scroll Position"). Derive it
    // from the saved page instead of a network total: (pageIndex + 1) reaches the end of the saved
    // page, the extra + 1 is a full-viewport buffer. The exact count from the first getRows overwrites it.
    const serverSideInitialRowCount = useMemo(() => {
      const { pageIndex = 0 } = getWorkspaceScrollPosition();

      return (pageIndex + 2) * perPage;
    }, [perPage]);

    useEffect(() => {
      const loaded = isFirstDataFetched && !isDatasourceRequestInProgress;
      const hasNoData = documentsCount === 0;
      const hasData = documentsCount > 0;

      if (loaded && hasNoData) {
        gridApiRef.current.showNoRowsOverlay();
      }

      if (loaded && hasData) {
        gridApiRef.current.hideOverlay();
      }
    }, [documentsCount, isFirstDataFetched, isDatasourceRequestInProgress]);

    useEffect(() => {
      if (
        rawColumnDefs.length > 0 &&
        initialColumnState.length > 0 &&
        !isEqual(previousInitialColumnStateRef.current, initialColumnState)
      ) {
        previousInitialColumnStateRef.current = initialColumnState;
        previousColumnModelRef.current = initialColumnState;
        dispatch(documentsGridDefaultColumnsAppliedAction(initialColumnState));
      }
    }, [dispatch, initialColumnState, rawColumnDefs]);

    // !IMPORTANT - dont change order of useEffect below for applying grid state
    useEffect(() => {
      if (isGridInit) {
        if (!isEqual(previousFilterModelRef.current, gridFilters)) {
          gridApiRef.current.setFilterModel(gridFilters);
          filterModelRef.current = gridFilters;
          previousFilterModelRef.current = gridFilters;
        }
      }
    }, [isGridInit, gridFilters]);

    useEffect(() => {
      if (isGridInit) {
        if (!isEqual(previousColumnModelRef.current, gridColumnsState)) {
          // skip sort props - we apply sort via gridSorting state
          const state = invertPinnedWhenRTL(gridColumnsState).map(({ colId, pinned, width, hide, flex }) => ({
            colId,
            pinned,
            width,
            hide,
            flex,
          }));

          gridApiRef.current.applyColumnState({
            state,
            applyOrder: true,
          });
          previousColumnModelRef.current = gridColumnsState;
        }
      }
    }, [isGridInit, gridColumnsState, invertPinnedWhenRTL]);

    useEffect(() => {
      if (isGridInit) {
        if (!isEqual(previousSortModelRef.current, gridSorting)) {
          const previousSort = previousSortModelRef.current[0];
          const currentSort = gridSorting[0];

          const state = [
            {
              sort: null,
              colId: previousSort?.colId,
            },
            {
              sort: currentSort?.sort || null,
              colId: currentSort?.colId,
            },
          ].filter(({ colId }) => Boolean(colId));

          gridApiRef.current.applyColumnState({
            state,
          });

          previousSortModelRef.current = gridSorting;
        }
      }
    }, [isGridInit, gridSorting]);

    useEffect(() => {
      if (gridApiRef.current && documentsOrderedMapRef.current.size > 0) {
        // this is for cmd/ctrl+A feature, listener is placed in BulkPanelComponent,
        // 'api' - send this source name for event, and skip handler for this
        // reset selected rows when we have empty array list
        if ((selected.size === 0 && previousSelectedSize) || isLinkedPanelSelected) {
          gridApiRef.current.deselectAll('api');
          selectedIDsRef.current = OrderedSet();
        } else if (selected.size === documentsOrderedMapRef.current.size && previousSelectedSize !== selected.size) {
          gridApiRef.current.selectAll('api');
          selectedIDsRef.current = OrderedSet(selected);
        }
      }
    }, [selected, previousSelectedSize, isLinkedPanelSelected]);

    // used for refresh data(silent mode - no request sent to backend) of grid after some changes for example moving to another category etc
    // dispatched in saga mostly
    useEffect(() => {
      const { nodes, transactionType } = refreshDocumentNodes;
      if (transactionType && nodes.size > 0) {
        const data = documentsGridRowNodesAdapter(refreshDocumentNodes.nodes);
        const transaction = {
          [refreshDocumentNodes.transactionType]: data,
        };

        const { status } = gridApiRef.current.applyServerSideTransaction(transaction);

        if (transactionType === 'remove' && status === 'Applied') {
          documentsOrderedMapRef.current = documentsOrderedMapRef.current.filter((d) => !nodes.includes(d.documentID));
          // DA-19691: the soft transaction drops rows without re-running getRows, so Redux `count`
          // (documentsCount) is not refreshed. Show the empty-state stub off the grid's own displayed
          // row count, so emptying the category via a move/delete renders it (there is no redirect now).
          if (gridApiRef.current.getDisplayedRowCount() === 0) {
            gridApiRef.current.showNoRowsOverlay();
          }
        }
        dispatch(documentsGridApplyTransactionAction(GridApplyTransactionFactory()));
      }
    }, [refreshDocumentNodes, dispatch]);

    // used for refresh data(hard mode - request sent to backend) of grid after some changes for example moving to another category etc
    useEffect(() => {
      if (typeof refreshServerSide === 'boolean') {
        gridApiRef.current.deselectAll();
        gridApiRef.current.refreshServerSide({ purge: refreshServerSide });
        dispatch(agGridRefreshServerSideAction(null));
      }
    }, [refreshServerSide, dispatch]);

    useEffect(() => {
      // when we come to document page from grid view and navigate between documents
      // we can return to the grid view, set Ref value from redux for navigate to correct page on grid
      if (!gridSearchAfterByPageIndex.equals(searchAfterByPageIndexRef.current)) {
        searchAfterByPageIndexRef.current = gridSearchAfterByPageIndex;
      }
    }, [gridSearchAfterByPageIndex]);

    // refresh grid data when category changed and skip it on first render
    useDidUpdateEffect(() => {
      gridApiRef.current?.deselectAll();
      gridApiRef.current?.refreshServerSide({ purge: true });
    }, [category]);

    // refresh grid data when [location search (URL query params)] changed and skip it on first render
    useDidUpdateEffect(() => {
      gridApiRef.current.refreshServerSide({ purge: false });
    }, [search]);

    const getRowID = useCallback(({ data }) => data.documentID, []);

    const isRowMaster = useCallback((dataItem) => dataItem.hasMasterDetail, []);

    const dispatchColumnState = useCallback(() => {
      if (gridApiRef.current) {
        const columns = gridApiRef.current.getColumnState();
        // we save to store always LTR version of grid state
        const stateLTR = invertPinnedWhenRTL(columns);
        // we compare with gridColumnState in ltr variant, so set to previousColumnModelRef.current ltr variant as well
        previousColumnModelRef.current = stateLTR;
        dispatch(documentsGridColumnsAppliedAction(stateLTR));
      }
    }, [dispatch, invertPinnedWhenRTL]);

    const onSelectionChanged = useCallback(
      ({ api, source }) => {
        // event source 'api' is set in useEffect
        if (!isLinkedPanelSelected || source !== 'api') {
          let selectedIDs;
          const { selectAll, toggledNodes } = api.getServerSideSelectionState();
          const documentIDs = documentsOrderedMapRef.current.keySeq().toOrderedSet();

          if (selectAll) {
            if (toggledNodes.length > 0) {
              selectedIDs = OrderedSet(documentIDs).subtract(toggledNodes);
            } else {
              selectedIDs = OrderedSet(documentIDs);
            }
          } else {
            selectedIDs = OrderedSet(toggledNodes);
          }

          if (!selectedIDs.equals(selectedIDsRef.current)) {
            selectedIDsRef.current = selectedIDs;
            resetIsLinkedPanelSelected();
            onChangeSelected(selectedIDs);
          }
        }
      },
      [onChangeSelected, resetIsLinkedPanelSelected, isLinkedPanelSelected],
    );

    const onRowGroupOpened = useCallback((e) => {
      if (e.expanded && gridApiRef.current) {
        gridApiRef.current.forEachNode((node) => {
          if (node.expanded && e.data.documentID !== node.data.documentID) {
            node.setExpanded(false);
          }
        });
      }
    }, []);

    const handleChangeColumnState = useMemo(
      () =>
        debounce(250, ({ type, source }) => {
          // dispatch column state only when done manually
          // dragStopped - column drag, resize column
          // autosizeColumns - via column menu (generalMenuTab)
          if (type === 'dragStopped' || source === 'autosizeColumns') {
            dispatchColumnState();
          }
        }),
      [dispatchColumnState],
    );

    const onColumnPinned = useCallback(
      ({ source }) => {
        // dispatch column state only when done manually via menu
        // skip when we apply state via api
        if (source === 'columnMenu') {
          dispatchColumnState();
        }
      },
      [dispatchColumnState],
    );

    const findColumnToUnpin = useCallback(
      (columnState, pinPosition) =>
        columnState.find((col) => !col.colId.startsWith('__SERVICE') && col.pinned === pinPosition),
      [],
    );

    const handleProcessUnpinnedColumns = useCallback(
      ({ api }) => {
        const columnState = api.getColumnState();

        let columnToUnpin = findColumnToUnpin(columnState, enableRtl ? 'left' : 'right');

        if (!columnToUnpin) {
          columnToUnpin = findColumnToUnpin(columnState, enableRtl ? 'right' : 'left');
        }

        if (columnToUnpin) {
          api.setColumnsPinned([columnToUnpin.colId], null);
          dispatchColumnState();
        }
      },
      [dispatchColumnState, enableRtl, findColumnToUnpin],
    );

    const onColumnMenuVisibleChanged = useCallback(
      ({ visible, key, api }) => {
        // when click on column menu button for management columns -
        // event return sometimes key generalMenuTab instead of columnsMenuTab
        if (visible === false && ['columnsMenuTab', 'generalMenuTab'].includes(key)) {
          const state = api.getColumnState();
          const previousHiddenColumn = previousColumnModelRef.current.filter((col) => col.hide === true);
          const currentHiddenColumn = state.filter((col) => col.hide === true);

          if (!isEqual(previousHiddenColumn, currentHiddenColumn)) {
            dispatchColumnState();
          }
        }
      },
      [dispatchColumnState],
    );

    const onClickOpen = useCallback(
      (_, doc) => {
        onDocumentOpen(doc.documentID);
      },
      [onDocumentOpen],
    );

    const frameworkComponents = useMemo(
      () => ({
        masterViewCellRenderer: MasterViewCellRenderer,
        approvalsCellRenderer: ApprovalsCellRenderer,
        tagsCellRenderer: TagsCellRenderer,
        amountCellRenderer: AmountCellRenderer,
        amountSumStatusPanel: AmountSumStatusPanelRenderer,
        previewCellRenderer: PreviewCellRenderer,
        linkedCellRenderer: LinkedCellRenderer,
        alertCellRenderer: AlertCellRenderer,
        contextCellRenderer: ContextCellRenderer,
        customSetColumnFilter: CustomSetColumnFilter,
        agColumnHeader: AgGridColumnHeader,
        customDateFilter: AgDateFilter,
      }),
      [],
    );

    const serverDatasource = useMemo(
      () => ({
        getRows: async (params) => {
          const {
            context: { fetchParams },
            success,
            fail,
            request,
            api,
          } = params;
          const currentPageIndex = api.paginationGetCurrentPage();
          const { filterModel: requestFilterModel, sortModel, startRow, endRow } = request;
          const requestPageIndex = startRow / (endRow - startRow);
          // sometimes we can caught case when filter model(width custom/async filters) not applied
          // we got empty object instead of object with applied filter model
          // for safety, check of the applied model has been added
          const isAppliedFilterModel = isEqual(filterModelRef.current, requestFilterModel);

          if (isAppliedFilterModel) {
            filterModelRef.current = null;
          }

          const filterModel =
            filterModelRef.current && !isAppliedFilterModel ? filterModelRef.current : requestFilterModel;

          const requestData = {
            filterModel,
            sortModel,
            startRow,
            endRow,
          };

          api.hideOverlay();
          api.clearCellSelection();

          const isSortEqual = isEqual(previousSortModelRef.current, sortModel);
          const isFilterEqual = isEqual(previousFilterModelRef.current, filterModel);

          if (!isSortEqual) {
            // order important, set to previous, after dispatch to redux
            previousSortModelRef.current = sortModel;
            dispatch(
              documentsGridSortingAppliedAction({
                sorting: sortModel,
              }),
            );
          }

          if (!isFilterEqual) {
            previousFilterModelRef.current = filterModel;
            dispatch(
              documentsGridFilterAppliedAction({
                filters: filterModel,
              }),
            );
          }

          setDatasourceRequestInProgress(true);

          try {
            const { data } = await Api.getWorkspaceGridRows({
              data: {
                ...fetchParams,
                ...requestData,
                searchAfter:
                  currentPageIndex > 0
                    ? searchAfterByPageIndexRef.current.get((currentPageIndex - 1).toString())
                    : null,
              },
            });

            const { items, count, timeStamp, searchAfter } = data;

            const adaptedList = gridDocumentsListAdapter(items);
            const rowData = documentsGridRowNodesAdapter(adaptedList);
            const response = { rowData, rowCount: count };

            success(response);

            documentsOrderedMapRef.current = adaptedList;
            searchAfterByPageIndexRef.current = searchAfterByPageIndexRef.current.set(
              currentPageIndex.toString(),
              List(searchAfter),
            );

            dispatch({
              type: documentsGridGetRowsAction.success,
              payload: {
                list: adaptedList,
                count,
                timeStamp,
                count_without_link_panels: count,
              },
            });

            dispatch(
              documentsGridDocumentsByPageIndexAction({
                data: Map().set(requestPageIndex.toString(), adaptedList.keySeq().toList()),
                purge: true,
              }),
            );

            dispatch(
              documentsGridSearchAfterByPageIndexAction({
                purge: false,
                data: Map().set(currentPageIndex.toString(), List(searchAfter)),
              }),
            );

            setDatasourceRequestInProgress(false);
            setIsFirstDataFetched(true);
          } catch (error) {
            console.log('====== REQUEST FAILED', error);
            fail();
          }
        },
      }),
      [dispatch], // be careful when adding dependencies to hook - potentially invoked again every time values change
    );

    const onGridReady = useCallback(async (params) => {
      gridApiRef.current = params.api;
      setGridInit(true);
    }, []);

    const onFirstDataRendered = useCallback(() => {
      onFirstDataRenderedRef.current = true;
    }, []);

    const onPaginationChanged = useCallback(({ newPage, newPageSize, api }) => {
      if (newPageSize) {
        const size = api.paginationGetPageSize();

        api.deselectAll();
        setPerPage(size);
        setWorkspaceGridPerPage(size);
      }

      if (newPage) {
        api.deselectAll();
      }
    }, []);

    const onSortChanged = useCallback(
      ({ api, source }) => {
        const currentPageIndex = api.paginationGetCurrentPage();

        // for b-end pagination we must reset user to the first page when start manual sorting
        if (source === 'uiColumnSorted' && currentPageIndex > 0) {
          dispatch(
            documentsGridSearchAfterByPageIndexAction({
              purge: true,
              data: Map(),
            }),
          );

          searchAfterByPageIndexRef.current = Map();

          api.paginationGoToFirstPage();
        }
      },
      [dispatch],
    );

    const onStateUpdated = useCallback(({ state, sources, api }) => {
      // console.log('====== ON STATE UPDATED', state, sources);
    }, []);

    const { startIndex, handlePreviewNavigate, handleChangePreview, isPreviewOpen } =
      useAgGridDocumentsPreviewNavigation({
        gridApiRef,
        isGridReady: isGridInit,
        list: documentsList,
        previewContextKey: PREVIEW_CONTEXT_NAME,
        previewState: preview,
        onPreviewCallback: onPreview,
      });

    const componentsContext = useMemo(
      () => ({
        previewCellRenderer: {
          onClickPreview: handleChangePreview,
          onClickDocument: onDocumentOpen,
        },
        linkedCellRenderer: {
          onClickLinked: onShowLinked,
        },
        contextCellRenderer: {
          oncClickContextMenu: onContextMenu,
        },
      }),
      [handleChangePreview, onDocumentOpen, onShowLinked, onContextMenu],
    );

    const gridContext = useGridContext({
      components: componentsContext,
      autocompleteApiInstance: Api.getDocumentsGridFilterListByField,
    });

    usePropagateGridApi({ ref, gridApiRef, gridContext, selectedIDs: selected });

    useScrollManager({
      view: 'grid',
      isTargetLoaded: isFirstDataFetched,
      gridApi: gridApiRef.current,
    });

    // !IMPORTANT - be careful when changing next condition, we must wait for this data for grid to work correctly
    if (!isGridPresetLoaded || !isCompanyConfigurationLoaded || rawColumnDefsIsBusy) {
      return null;
    }

    return (
      <>
        <AgGridGlobalStyles />
        <AgGridDocumentsStyles />
        <Box className="ag-theme-dokka" display="flex" flexDirection="column" height="100%">
          <AgGridReact
            theme={agGridTheme}
            serverSideDatasource={serverDatasource}
            masterDetail={hasMasterDetail}
            isRowMaster={isRowMaster}
            components={frameworkComponents}
            detailRowHeight={400}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onStateUpdated={onStateUpdated}
            onRowGroupOpened={onRowGroupOpened}
            onColumnPinned={onColumnPinned}
            onSelectionChanged={onSelectionChanged}
            onDragStopped={handleChangeColumnState}
            onCellSelectionChanged={onCellSelectionChanged}
            onColumnResized={handleChangeColumnState}
            onColumnMenuVisibleChanged={onColumnMenuVisibleChanged}
            onPaginationChanged={onPaginationChanged}
            onSortChanged={onSortChanged}
            // props from useAgGridManualConfig hook
            enableRtl={enableRtl}
            getLocaleText={getLocaleText}
            rowClassRules={rowClassRules}
            // props from useAgGridDocumentsProps hook
            {...agGridDocumentsProps}
            rowModelType="serverSide"
            suppressServerSideFullWidthLoadingRow
            columnTypes={columnTypes}
            getRowId={getRowID}
            rowHeight={58}
            columnDefs={columnDefs}
            context={gridContext}
            cacheBlockSize={perPage}
            maxBlocksInCache={1}
            initialState={initialState}
            noRowsOverlayComponent={StubWhenEmpty}
            serverSideInitialRowCount={serverSideInitialRowCount} // saved-page estimate; exact count arrives from the first getRows
            pagination
            paginationPageSize={perPage}
            paginationPageSizeSelector={GRID_PAGE_SIZE}
            processUnpinnedColumns={handleProcessUnpinnedColumns}
          />
        </Box>

        {isPreviewOpen && (
          <DocumentPreviewNavigation
            documentId={preview.documentId}
            setPreview={(document) => handleChangePreview(document?.documentID)}
            list={documentsList}
            allCount={documentsCount}
            onContextMenu={onContextMenu}
            onClick={onClickOpen}
            startIndex={startIndex}
            onNavigate={handlePreviewNavigate}
          />
        )}
      </>
    );
  },
);

export default function ({ forwardedRef, ...props }) {
  return <Grid ref={forwardedRef} {...props} />;
}
